import React, { useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Tooltip,
  Text
} from "@chakra-ui/react";
import { DataFrameColumnProto, KGDataFrameProto } from "build-ts/dataframe";
import { FormatProto } from "build-ts/metadata";
import { useUser } from "context/UserContext";
import { BenchmarkType, ColumnType, columnTypeFromJSON } from "build-ts/enums";
import { epochConverterSecondWithTimeZone } from "utils/dateUtils";
import { getDateFilterName } from "utils/nameUtils";

interface DataFrameTableProps {
  data: KGDataFrameProto;
  setNumberOfRows: React.Dispatch<React.SetStateAction<number>>;
  showHiddenColumns?: boolean;
}

interface ProcessedColumn {
  name: string;
  versus_filters?: string[];
  values: any[];
  eval_expression?: string;
  is_aggregate_expression?: boolean;
  is_visible: boolean;
  format?: FormatProto;
  colType?: ColumnType; // Need to store column type to format temporal columns correctly.
}

/**
 * Formats a value based on the provided format specification
 * Also formats temporal columns correctly.
 */
const formatValue = (value: any, format?: FormatProto, columnType?: ColumnType, defaultTimeZone?: string, isEvalExpression?: boolean): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  // for expression column values, we need to format the value to 2 decimals
  if (isEvalExpression) {
    // Convert string to number if needed, then format to 2 decimals
    if (typeof value === "number") {
      return value.toFixed(2);
    }
    return String(value);
  }

  if (!format) {
    // only format temporal columns if the value is a number and not a string
    if (columnTypeFromJSON(columnType) === ColumnType.TEMPORAL && typeof value === "number") {
      return epochConverterSecondWithTimeZone(value, "dd-MMM-yyyy", defaultTimeZone!);
    }
    return String(value);
  }

  // For numeric values
  if (typeof value === "number" || !isNaN(Number(value))) {
    const numValue = typeof value === "number" ? value : Number(value);
    const decimals = format.num_decimals ?? 2;

    // If currency_code is present, format as currency (not with percentage)
    if (format.currency_code) {
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: format.currency_code,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue);
      return format.suffix ? `${formatted}${format.suffix}` : formatted;
    }

    // If percentage is true, multiply by 100 and add %
    if (format.percentage) {
      const percentValue = (numValue * 100).toFixed(decimals);
      return format.suffix ? `${percentValue}%${format.suffix}` : `${percentValue}%`;
    }

    // Regular number formatting
    const formatted = numValue.toFixed(decimals);
    return format.suffix ? `${formatted}${format.suffix}` : formatted;
  }

  // For non-numeric values, just add suffix if present
  return format.suffix ? `${value}${format.suffix}` : String(value);
};

/**
 * Finds the format for a column by matching actuals_id with metric header id
 */
const findFormatForColumn = (
  column: DataFrameColumnProto,
  data: KGDataFrameProto
): FormatProto | undefined => {
  if (!column.column_hash || !data.kg_observation) {
    return undefined;
  }

  const actualsId = column.column_hash.actuals_id;
  if (!actualsId) {
    return undefined;
  }

  const metric = data.kg_observation.metrics.find(
    (m) => m.header?.id === actualsId
  );

  return metric?.format;
};

const DataFrameTable: React.FC<DataFrameTableProps> = ({ data, setNumberOfRows, showHiddenColumns = false }) => {

  const { defaultTimeZone } = useUser();

  // Process regular columns (non-aggregate)
  const processedColumns: ProcessedColumn[] = data.ordered_columns
    .filter((column) => !column.is_aggregate_expression)
    .filter((column) => showHiddenColumns || column.is_visible !== false)
    .map((column) => {
      const name = column.output_column_name || "";

      let values: any[] = [];
      if (column.data_value_json_array) {
        try {
          // Replace NaN, Infinity, -Infinity with null since they're not valid JSON
          const sanitizedJson = column.data_value_json_array
            .replace(/\bNaN\b/g, "null")
            .replace(/\bInfinity\b/g, "null")
            .replace(/\b-Infinity\b/g, "null");
          values = JSON.parse(sanitizedJson);
        } catch (error) {
          console.error(`Failed to parse data_value_json_array for column ${name}:`, error);
          console.error("Problematic data:", column.data_value_json_array);
          values = [];
        }
      }

      const is_visible = column.is_visible !== undefined ? column.is_visible : true;
      const eval_expression = column.eval_expression;
      const is_aggregate_expression = column.is_aggregate_expression;
      const format = findFormatForColumn(column, data);
      const colType = data.kg_observation?.columns.find(
        (c) => c.column_id === column.column_hash?.actuals_id
      )?.kg_column_header?.column_type;
      const benchmark_id = column.column_hash?.benchmark_id;

      // filter modification for comparison columns for display
      const benchmark_filters: string[] = [];
      if (benchmark_id) {
        const benchmark = data.kg_observation?.benchmarks.find(
          (b) => b.header?.id === benchmark_id
        );
        if (benchmark?.benchmark_type === BenchmarkType.VERSUS_BENCHMARK) {
          benchmark.versus_filters?.forEach((filter) => {
            benchmark_filters.push(getDateFilterName(filter?.date_filter!));
          });
        }
      }
      const versus_filters: string[] = benchmark_filters;

      return {
        name,
        versus_filters,
        values,
        eval_expression,
        is_aggregate_expression,
        is_visible,
        format,
        colType,
      };
    });

  // Get number of rows
  const numRows = processedColumns[0]?.values.length || 0;

  // Report row count to parent after render; never call setState during render
  useEffect(() => {
    setNumberOfRows(numRows);
  }, [numRows, setNumberOfRows]);

  return (
    <Box width="100%">
      <Table variant="simple" size="sm">
        <Thead
          position="sticky"
          top={0}
          zIndex={10}
          boxShadow="sm"
        >
          <Tr>
            {processedColumns.map((col, index) => (
              <Th
                key={index}
                bg={!col.is_visible ? "gray.50" : "gray.100"}
                color={!col.is_visible ? "gray.600" : "gray.900"}
                borderBottomWidth="2px"
                borderBottomColor="gray.300"
              >
                {col.eval_expression ? (
                  <Tooltip
                    label={col.eval_expression}
                    hasArrow
                    placement="top"
                    bg="gray.700"
                    fontSize="xs"
                  >
                    <Box as="span" cursor="pointer" minWidth="100px" maxWidth="200px">
                      {col.name}
                    </Box>
                  </Tooltip>
                ) : (
                  <Box as="span" cursor="pointer" minWidth="100px" maxWidth="200px">
                    {col.versus_filters && col.versus_filters.length > 0 ? (
                      <Tooltip
                        label={col.versus_filters.join(", ")}
                        hasArrow
                        placement="top"
                        bg="gray.700"
                        fontSize="xs"
                      >
                        <Box as="span" cursor="pointer" minWidth="100px" maxWidth="200px">
                          {col.name}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Box as="span" cursor="pointer" minWidth="100px" maxWidth="200px">
                        <Box as="span" cursor="pointer" minWidth="100px" maxWidth="200px">
                          {col.name}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {numRows === 0 ? (
            <Tr>
              <Td colSpan={processedColumns.length} textAlign="center" color="gray.500" fontSize="sm" py={3} px={3}>
                No rows found
              </Td>
            </Tr>
          ) : null}
          {Array.from({ length: numRows }).map((_, rowIndex) => (
            <Tr key={rowIndex}>
              {processedColumns.map((col, colIndex) => {
                const rawValue = col.values[rowIndex];
                const isEvalExpression = col.eval_expression ? true : false;
                const formattedValue = formatValue(rawValue, col.format, col.colType, defaultTimeZone, isEvalExpression);

                return (
                  <Td
                    key={colIndex}
                    bg={!col.is_visible ? "gray.50" : "white"}
                    color={!col.is_visible ? "gray.900" : "gray.900"}
                    maxWidth="350px"
                    fontWeight="600"
                  >
                    <Text
                      noOfLines={4}
                      whiteSpace="normal"
                      m={0}
                      title={formattedValue}
                      fontSize="xs"
                      overflowX="auto"
                    >
                      {formattedValue}
                    </Text>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default DataFrameTable;